import { useEffect, useState } from 'react';
import { Briefcase, MapPin, Star, Users } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Avatar, EmptyState, HeroHeader } from '@/components/ui';
import Spinner from '@/components/Spinner';
import styles from './Providers.module.css';

type ProviderRow = {
  id: string;
  full_name: string;
  city: string;
  rating: number | null;
  experience_years: number;
  is_approved: boolean;
  services: string;
};

export default function AdminProviders() {
  const [providers, setProviders] = useState<ProviderRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      try {
        const { data, error } = await supabase
          .from('providers')
          .select(
            `id, rating, experience_years, is_approved,
             profiles(full_name, phone, city),
             provider_services(services(name))`
          )
          .order('created_at', { ascending: false });

        if (!error && data) {
          setProviders(
            data.map((row: any) => ({
              id: row.id,
              full_name: row.profiles?.full_name ?? '—',
              city: row.profiles?.city ?? '—',
              rating: row.rating,
              experience_years: row.experience_years ?? 0,
              is_approved: Boolean(row.is_approved),
              services: (row.provider_services ?? [])
                .map((link: any) => link.services?.name)
                .filter(Boolean)
                .join(', '),
            }))
          );
        }
      } catch (err) {
        console.error('Error loading providers:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className={styles.page}>
      <HeroHeader title="Prestataires" subtitle={`${providers.length} inscrits`} />

      <div className={styles.body}>
        {loading ? (
          <Spinner block />
        ) : providers.length === 0 ? (
          <EmptyState icon={<Users size={30} />} title="Aucun prestataire" />
        ) : (
          <ul className={styles.list}>
            {providers.map((provider) => (
              <li key={provider.id} className={styles.card}>
                <div className={styles.cardHead}>
                  <Avatar name={provider.full_name} size={42} />
                  <p className={styles.name}>{provider.full_name}</p>
                  <span
                    className={`${styles.badge} ${
                      provider.is_approved ? styles.badgeApproved : styles.badgePending
                    }`}
                  >
                    {provider.is_approved ? 'Approuvé' : 'En attente'}
                  </span>
                </div>

                <p className={styles.row}>
                  <MapPin size={14} color="#666" /> {provider.city}
                </p>
                <p className={styles.row}>
                  <Briefcase size={14} color="#666" /> {provider.services || 'Aucun service'}
                </p>
                <p className={styles.row}>
                  <Star size={14} color="#FFA900" fill="#FFA900" />
                  {provider.rating != null ? Number(provider.rating).toFixed(1) : '—'} ·{' '}
                  {provider.experience_years} ans d&apos;exp.
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
